/**
 * Copyright schukai GmbH and contributors 2023. All Rights Reserved.
 * Node module: @schukai/monster
 * This file is licensed under the AGPLv3 License.
 * License text available at https://www.gnu.org/licenses/agpl-3.0.en.html
 */

import { Base } from "./base.mjs";
import { instanceSymbol } from "../constants.mjs";

export { Queue };

/**
 * A queue is a list of items that are processed one after another (first in, first out).
 *
 * With a queue you can add items to the end of the list `Queue.add()` and remove items from the beginning of the list `Queue.pop()`.
 *
 * With `Queue.peek()` you can get the first item without removing it from the list.
 *
 * You can create the instance via `new Queue()`.
 *
 * @externalExample ../../example/types/queue.mjs
 * @license AGPLv3
 * @since 1.4.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 * @summary A Queue (Fifo)
 */
class Queue extends Base {
	/**
	 *
	 */
	constructor() {
		super();
		this.data = [];
	}

	/**
	 * This method is called by the `instanceof` operator.
	 * @returns {symbol}
	 * @since 2.1.0
	 */
	static get [instanceSymbol]() {
		return Symbol.for("@schukai/monster/types/queue");
	}

	/**
	 * @return {boolean}
	 */
	isEmpty() {
		return this.data.length === 0;
	}

	/**
	 * Read the element at the front of the queue without removing it.
	 *
	 * @return {*}
	 */
	peek() {
		if (this.isEmpty()) {
			return undefined;
		}

		return this.data[0];
	}

	/**
	 * Add a new element to the end of the queue.
	 *
	 * @param {*} value
	 * @returns {Queue}
	 */
	add(value) {
		this.data.push(value);
		return this;
	}

	/**
	 * remove all entries
	 *
	 * @returns {Queue}
	 */
	clear() {
		this.data = [];
		return this;
	}

	/**
	 * Remove the element at the front of the queue
	 * If the queue is empty, return undefined.
	 *
	 * @return {*}
	 */
	poll() {
		if (this.isEmpty()) {
			return undefined;
		}
		return this.data.shift();
	}
}
